import { nameof } from "lib/actions/util";
import { useState } from "react";
import { useWizard } from "components/elements/Wizard/useWizard";
import { useSpeckleModels } from "../../lib/hooks/useSpeckleModels";
import { getModelTextSearch } from "lib/actions/getModelTextSearch";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { SpeckleImportModel } from "../types/SpeckleImportModel";
import { SpeckleModelModel } from "../types/SpeckleModelModel";
import SpeckleAutomateToggle from "../SpeckleAutomateToggle";
import Loading from "components/elements/Loading/Loading";
import FormError from "components/elements/Forms/FormError";
import SpeckleTitle from "../SpeckleTitle";
import InputSearch from "components/elements/Input/InputSearch";
import SpeckleRefreshButton from "../SpeckleRefreshButton";
import SpeckleBreadcrumbs, {
  SpeckleCrumb,
  SpeckleCrumbSeparator,
} from "../SpeckleBreadcrumbs";
import SpeckleRadioInputModels from "../controls/SpeckleRadioInputModels";
import SpeckleCreateModelForm from "./create/SpeckleCreateModelForm";
import Button from "components/elements/Button/Button";
import {
  getClearedValuesForStep,
  SpeckleImportStep,
} from "components/modules/UploadDialog/SpeckleImportWizard/steps/getClearedValuesForStep";

export default function SpeckleImportModelStep() {
  const [searchTerm, setSearchTerm] = useState("");
  const { values, isSubmitting, setValues, submitForm, setTouched, setErrors } =
    useFormikContext<SpeckleImportModel>();
  const { stepVariant, stepViewVariant, stepJump } = useWizard();
  const {
    models: modelsData,
    isLoading,
    hasError,
    refresh,
  } = useSpeckleModels(parseInt(values.connectedAccountId), values.projectId);

  function handleStepJump(step: SpeckleImportStep) {
    const clearedValues = getClearedValuesForStep(step, values);
    setValues(clearedValues, false);
    setTouched({}, false);
    setErrors({});
    stepJump(step);
  }

  const variantCreate = "create";
  const isShowingCreate = stepVariant === variantCreate;
  const project = values.projectName || "Project";
  const account = values.connectedAccountId || "Account";
  const { search } = getModelTextSearch<SpeckleModelModel>(
    searchTerm,
    (model) => model.name,
  );
  const models = search(modelsData) || [];
  const modelItems =
    !isLoading &&
    !hasError &&
    models.map((x) => ({
      value: x.model.id,
      label: x.model.name,
    }));

  function onRefresh() {
    refresh();
  }

  async function onSelect(value: string) {
    setValues(
      (values) => ({
        ...values,
        modelId: value,
        modelName: models.find((x) => x.model.id === value)?.model.name,
      }),
      false,
    );
    await new Promise((res) => setTimeout(res, 0)); //hack: cannot submit immediately after setting values
    submitForm();
  }

  function onShowCreate() {
    stepViewVariant(variantCreate);
  }

  async function onCreated(created: SpeckleModelModel) {
    setValues(
      (values) => ({
        ...values,
        modelId: created.id,
        modelName: created.name,
      }),
      false,
    );

    await new Promise((res) => setTimeout(res, 0)); //hack: cannot submit immediately after setting values
    submitForm();
  }

  return (
    <div className="grid grid-cols-2 gap-8">
      <div>
        <header>
          <SpeckleTitle />
          <h1 className="mt-10 flex w-full flex-col text-2xl font-thin leading-loose">
            <div>
              Select a <span className="font-normal">Model</span> from{" "}
            </div>
            <div className="line-clamp-3 w-5/6 overflow-hidden text-ellipsis break-words font-normal">
              {project}
            </div>
            <span className="flex whitespace-nowrap">
              or&nbsp;
              <Button
                type="button"
                className="my-auto ml-2 h-8 text-sm"
                disabled={isShowingCreate}
                onClick={onShowCreate}
              >
                create model
              </Button>
            </span>
          </h1>
        </header>
        {isShowingCreate && (
          <section className="mt-8 rounded-xl bg-gray-50 p-6 pb-10">
            <h2 className="font-medium">Create a model in&nbsp;Speckle</h2>
            <SpeckleCreateModelForm
              speckleProjectId={values.projectId}
              onCreated={onCreated}
            />
          </section>
        )}
      </div>
      <div className="space-y-5">
        <SpeckleBreadcrumbs>
          <SpeckleCrumb
            onClick={() => handleStepJump(SpeckleImportStep.Account)}
          >
            {account}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb
            onClick={() => handleStepJump(SpeckleImportStep.Workspace)}
          >
            {values.workspaceName}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb
            onClick={() => handleStepJump(SpeckleImportStep.Project)}
          >
            {project}
          </SpeckleCrumb>
          <SpeckleCrumbSeparator />
          <SpeckleCrumb active>Models</SpeckleCrumb>
        </SpeckleBreadcrumbs>
        <div className="flex items-center justify-between pr-5 text-xs">
          <span>{models.length} models available</span>
          <SpeckleRefreshButton onClick={onRefresh} />
        </div>
        <div className="h-[36rem] space-y-6 overflow-y-auto pb-8 pr-4">
          <SpeckleAutomateToggle
            connectedSpeckleAccountId={parseInt(values.connectedAccountId)}
            speckleProjectId={values.projectId}
          />
          <InputSearch
            defaultValue={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search models"
          />
          <Field
            name={nameof(values, "modelId")}
            as={SpeckleRadioInputModels}
            items={modelItems}
            project={project}
            onSelect={onSelect}
            disabled={isSubmitting || isShowingCreate}
          />
          {isLoading && <Loading />}
          {hasError && <span className="text-center">Error</span>}
        </div>
        <ErrorMessage name={nameof(values, "modelId")} component={FormError} />
      </div>
    </div>
  );
}
